import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import articles from '../content/blog.json';

const SITE_URL = 'https://standlogic.app';
const DEFAULT_DESCRIPTION =
  'Plan your whitetail hunts with morning and evening ratings based on weather, wind, pressure and moon conditions. Explore StandLogic for Android.';

type PublicPage = {
  title: string;
  description: string;
};

const PUBLIC_PAGES: Record<string, PublicPage | undefined> = {
  '/blog': {
    title: 'Blog | StandLogic',
    description: 'Observations, questions, and research on whitetail deer behavior, lunar timing, and feeding patterns.',
  },
  ...Object.fromEntries(articles.map((article) => [`/blog/${article.slug}`, {
    title: `${article.title} | StandLogic`,
    description: article.description,
  }])),
  '/': {
    title: 'StandLogic | Whitetail Hunting Outlook',
    description: DEFAULT_DESCRIPTION,
  },
  '/support': {
    title: 'Support | StandLogic',
    description: 'Learn how StandLogic hunting ratings work, compare Free and Plus, and get help with Google sign-in and your account.',
  },
  '/optin': {
    title: 'Become a Tester | StandLogic',
    description: 'Join the StandLogic testers Google Group to help test our Android app for planning whitetail hunts with weather and moon conditions.',
  },
  '/privacy': {
    title: 'Privacy Policy | StandLogic',
    description: 'Read how StandLogic collects, uses and protects your account, hunting location and subscription data.',
  },
  '/terms': {
    title: 'Terms of Service | StandLogic',
    description: 'Read the terms for using StandLogic, including accounts, subscriptions and hunting forecasts.',
  },
  '/disclaimer': {
    title: 'Disclaimer | StandLogic',
    description: 'Understand the limitations of StandLogic hunting outlooks and your responsibilities for hunting safety and local regulations.',
  },
  '/delete-account': {
    title: 'Delete Your Account | StandLogic',
    description: 'Learn how to delete your StandLogic account and associated data in the app or request deletion by email.',
  },
};

const PRIVATE_PAGE_TITLES: Record<string, string | undefined> = {
  '/setup': 'Profile Setup',
  '/auth/callback': 'Signing In',
  '/dashboard': 'Hunting Outlook',
  '/outlook': '7-Day Outlook',
  '/daily': 'Daily View',
  '/monthly': 'Monthly View',
  '/profile': 'Profile',
  '/admin': 'Admin',
  '/signin': 'Sign In',
};

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function DocumentMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
    const page = PUBLIC_PAGES[path];
    const privateTitle = PRIVATE_PAGE_TITLES[path];
    const title = page?.title ?? (privateTitle ? `${privateTitle} | StandLogic` : 'StandLogic');
    const description = page?.description ?? DEFAULT_DESCRIPTION;
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', page ? 'index, follow' : 'noindex, follow');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'StandLogic');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('name', 'twitter:card', 'summary');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (page) {
      const url = `${SITE_URL}${path}`;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = url;
      setMeta('property', 'og:url', url);
    } else {
      canonical?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }
  }, [pathname]);

  return null;
}

export default DocumentMetadata;
